import React, { useState } from 'react';
import './DrawingPanel.css';

const DrawingPanel = ({
  isDrawingMode,
  onClose,
  onStartDrawing,
  onSaveFeature,
  onDeleteFeature,
  drawnFeatures,
  currentDrawing
}) => {
  const [featureName, setFeatureName] = useState('');
  const [featureDescription, setFeatureDescription] = useState('');
  const [selectedType, setSelectedType] = useState('polygon');
  const [showSaveForm, setShowSaveForm] = useState(false);

  if (!isDrawingMode) return null;

  const handleStartDrawing = (type) => {
    setSelectedType(type);
    onStartDrawing(type);
  };

  const handleSave = () => {
    if (!currentDrawing) {
      alert('Please draw a feature first');
      return;
    }

    onSaveFeature({
      name: featureName || `Unnamed ${selectedType}`,
      description: featureDescription,
      type: selectedType
    });

    // Reset form
    setFeatureName('');
    setFeatureDescription('');
    setShowSaveForm(false);
  };

  const handleDelete = (featureId) => {
    if (window.confirm('Are you sure you want to delete this feature?')) {
      onDeleteFeature(featureId);
    }
  };

  return (
    <div className="drawing-panel">
      <div className="drawing-panel-header">
        <h3>✏️ Draw Features</h3>
        <button
          className="drawing-panel-close-btn"
          onClick={onClose}
          aria-label="Close drawing panel"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
      </div>

      <div className="drawing-panel-content">
        {/* Drawing Tools */}
        <div className="drawing-tools">
          <h4>Drawing Tools</h4>
          <div className="tool-buttons">
            <button
              className="tool-btn"
              onClick={() => handleStartDrawing('polygon')}
              title="Draw Polygon"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polygon points="12 2 2 7 12 12 22 7 12 2"></polygon>
                <polyline points="2 17 12 22 22 17"></polyline>
                <polyline points="2 12 12 17 22 12"></polyline>
              </svg>
              <span>Polygon</span>
            </button>

            <button
              className="tool-btn"
              onClick={() => handleStartDrawing('road')}
              title="Draw Road/Line"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="5" y1="12" x2="19" y2="12"></line>
                <polyline points="12 5 19 12 12 19"></polyline>
              </svg>
              <span>Road/Line</span>
            </button>

            <button
              className="tool-btn"
              onClick={() => handleStartDrawing('point')}
              title="Draw Point"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"></circle>
                <circle cx="12" cy="12" r="3"></circle>
              </svg>
              <span>Point</span>
            </button>
          </div>
        </div>

        {/* Current Drawing Info */}
        {currentDrawing && (
          <div className="current-drawing-info">
            <h4>Current Drawing</h4>
            <p>Type: <strong>{selectedType}</strong></p>
            
            {!showSaveForm ? (
              <button 
                className="save-drawing-btn"
                onClick={() => setShowSaveForm(true)}
              >
                💾 Save Feature
              </button>
            ) : (
              <div className="save-form">
                <input
                  type="text"
                  placeholder="Feature name"
                  value={featureName}
                  onChange={(e) => setFeatureName(e.target.value)}
                  className="drawing-input"
                />
                <textarea
                  placeholder="Description (optional)"
                  value={featureDescription}
                  onChange={(e) => setFeatureDescription(e.target.value)}
                  className="drawing-textarea"
                  rows="3"
                />
                <div className="save-form-buttons">
                  <button onClick={handleSave} className="btn-save">
                    Save
                  </button>
                  <button 
                    onClick={() => setShowSaveForm(false)} 
                    className="btn-cancel"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Saved Features List */}
        <div className="saved-features">
          <h4>Saved Features ({drawnFeatures.length})</h4>
          {drawnFeatures.length === 0 ? (
            <p className="no-features">No saved features yet</p>
          ) : (
            <div className="features-list">
              {drawnFeatures.map((feature) => (
                <div key={feature.id} className="feature-item">
                  <div className="feature-info">
                    <strong>{feature.properties.name}</strong>
                    <span className="feature-type">{feature.properties.feature_type}</span>
                  </div>
                  <button
                    onClick={() => handleDelete(feature.id)}
                    className="btn-delete"
                    title="Delete feature"
                  >
                    🗑️
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="drawing-instructions">
          <h4>Instructions</h4>
          <ul>
            <li><strong>Polygon:</strong> Click to add points, double-click to finish</li>
            <li><strong>Road/Line:</strong> Click to add points, double-click to finish</li>
            <li><strong>Point:</strong> Click once to place point</li>
            <li>Press <kbd>Escape</kbd> to cancel current drawing</li>
            <li>Click <strong>Save Feature</strong> to save to database</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default DrawingPanel;

